<template>
  <SanadeusPage>
    <template #header>
      <SanadeusPageHeader>
        <template #title>Übersicht</template>
      </SanadeusPageHeader>
    </template>
    <template #content>
      <div
        class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-y-10 lg:tw-gap-y-12 lg:tw-gap-x-8 tw-px-4 lg:tw-px-0 tw-mt-6 lg:tw-mt-0">
        <div class="lg:tw-col-start-1 lg:tw-row-start-1">
          <MonthPicker v-model="selectedmonth" />
          <DoughnutChart
            :counter="doughnutchartdata.numberOfEntries"
            :data="doughnutchartdata.statistics"
            class="tw-mt-6" />
        </div>
        <div class="lg:tw-col-start-1 lg:tw-row-start-2">
          <div class="tw-flex tw-flex-row tw-justify-between tw-items-end">
            <H2>Gesamt offen</H2>
            <template v-if="$apollo.queries.month_statistics.loading">
              <v-skeleton-loader
                class="tw-w-24 tw-ml-auto"
                height="23"
                type="text" />
            </template>
            <template v-else>
              <Amount :value="totalopen" class="tw-font-extrabold" />
            </template>
          </div>
          <StatusLine
            :loading="$apollo.queries.openpayments.loading"
            :portions="openportions"
            class="tw-mt-2"
            stroke="2" />
        </div>
        <div class="lg:tw-col-start-1 lg:tw-row-start-3">
          <H2>Versandte Abrechnungen</H2>
          <div class="tw-relative">
            <FilesDashboard
              :items="sentfiles"
              :loading="$apollo.queries.sentfiles.loading"
              class="tw-pointer-events-none"
              showStatusLine />
            <router-link :to="{ name: 'billings.sent' }">
              <span class="tw-inset-0 tw-absolute" />
              <span
                class="tw-text-ci-green tw-font-bold tw-mt-5 tw-inline-block">
                Alle versandten Abrechnungen
              </span>
            </router-link>
          </div>
        </div>
        <div class="lg:tw-col-start-2 lg:tw-row-start-1">
          <H2>Die letzten 12 Monate</H2>
          <BarChart :data="barchartdata" class="tw-mt-6 tw-h-44" />
        </div>
        <div class="lg:tw-col-start-2 lg:tw-row-start-2">
          <div class="tw-flex tw-flex-row tw-justify-between tw-items-end">
            <H2>Gesamt letzte 12 Monate</H2>
            <template v-if="$apollo.queries.month_statistics.loading">
              <v-skeleton-loader
                class="tw-w-28 tw-ml-auto"
                height="23"
                type="text" />
            </template>
            <template v-else>
              <Amount :value="totalvolume" class="tw-font-extrabold" />
            </template>
          </div>
          <StatusLine
            :portions="[{ color: '#EDEDED', proportion: '1' }]"
            class="tw-mt-2"
            stroke="2" />
        </div>
        <div class="lg:tw-col-start-2 lg:tw-row-start-3">
          <H2>Anstehende Abrechnungen</H2>
          <div class="tw-relative">
            <FilesDashboard
              :items="processedfiles"
              :loading="$apollo.queries.processedfiles.loading"
              class="tw-pointer-events-none" />
            <router-link :to="{ name: 'billings.pending' }">
              <span class="tw-inset-0 tw-absolute" />
              <span
                class="tw-text-ci-green tw-font-bold tw-mt-5 tw-inline-block">
                Alle anstehenden Abrechnungen
              </span>
            </router-link>
          </div>
        </div>
      </div>
    </template>
  </SanadeusPage>
</template>

<script>
import FilesDashboard from '@/components/Billings/FilesDashboard.vue'
import SanadeusPageHeader from '@/components/SanadeusPage/SanadeusPageHeader.vue'
import H2 from '@/components/Typography/H2.vue'
import BillingsDashboardQuery from '@/gql/queries/DashboardBillings.graphql'
import moment from 'moment'
import { mapGetters } from 'vuex'
import BarChart from '../components/Dashboard/BarChart.vue'
import DoughnutChart from '../components/Dashboard/DoughnutChart.vue'
import MonthPicker from '../components/helper/MonthPicker.vue'
import StatusLine from '../components/helper/StatusLine.vue'
import SanadeusPage from '../components/SanadeusPage/SanadeusPage.vue'

export default {
  components: {
    H2,
    SanadeusPage,
    SanadeusPageHeader,
    StatusLine,
    BarChart,
    DoughnutChart,
    MonthPicker,
    FilesDashboard,
  },
  data: function () {
    return {
      sentfiles: [],
      processedfiles: [],
      selectedmonth: moment().subtract(1, 'month').toDate(),
      month_statistics: [],
      openpayments: {
        open: 0,
        due: 0,
        overdue: 0,
      },
    }
  },
  computed: {
    ...mapGetters(['selectedNode']),
    totalopen: function () {
      return [
        this.openpayments.open,
        this.openpayments.due,
        this.openpayments.overdue,
      ].reduce((sum, cur) => sum + cur, 0)
    },
    openportions: function () {
      return [
        {
          color: '#82C8E2',
          proportion: this.openpayments.open,
        },
        {
          color: '#EF8305',
          proportion: this.openpayments.due,
        },
        {
          color: '#F82C06',
          proportion: this.openpayments.overdue,
        },
      ]
    },
    doughnutchartdata: function () {
      if (!this.selectedmonth_statistics) {
        return {
          open: 0,
          due: 0,
          overdue: 0,
          paid: 0,
          total: 0,
          numberOfEntries: 0,
        }
      }
      return this.selectedmonth_statistics
    },
    barchartdata: function () {
      const months = [
        '',
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'Mai',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Okt',
        'Nov',
        'Dez',
      ]

      const statsMap = this.month_statistics.reduce((acc, data) => {
        acc[data.month] = data
        return acc
      }, {})

      const now = new Date()
      const result = []

      for (let i = 12; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const year = date.getFullYear()
        const monthNum = date.getMonth() + 1
        const key = `${year}-${String(monthNum).padStart(2, '0')}`
        const label = months[monthNum]

        result.push({
          value: statsMap[key]?.statistics?.total ?? 0,
          month: label,
        })
      }

      return result
    },
    totalvolume: function () {
      return this.month_statistics.reduce((acc, cur) => {
        acc += cur.statistics.total
        return Math.round(acc * 100) / 100
      }, 0)
    },
  },
  apollo: {
    processedfiles: {
      query: BillingsDashboardQuery,
      variables: function () {
        return {
          node: this.selectedNode,
          state: 'processed',
          limit: 3,
        }
      },
      fetchPolicy: 'network-only',
      update: (data) => data.node_files,
    },
    sentfiles: {
      query: BillingsDashboardQuery,
      variables: function () {
        return {
          node: this.selectedNode,
          state: 'sent',
          processState: 'VALID',
          limit: 3,
        }
      },
      fetchPolicy: 'network-only',
      update: (data) => data.node_files,
    },
    month_statistics: {
      query: require('@/gql/queries/monthStatistics.graphql'),
      variables() {
        return {
          node: this.selectedNode,
          from: moment().subtract(12, 'month').startOf('month').toDate(),
          to: moment().toDate(),
        }
      },
      fetchPolicy: 'network-only',
    },
    selectedmonth_statistics: {
      query: require('@/gql/queries/monthStatistics.graphql'),
      variables() {
        return {
          node: this.selectedNode,
          from: moment(this.selectedmonth).startOf('month').toDate(),
          to: moment(this.selectedmonth).endOf('month').toDate(),
        }
      },
      fetchPolicy: 'network-only',
      update: (data) => {
        if (data?.month_statistics) {
          return data.month_statistics[0]
        }
        return {}
      },
    },
    openpayments: {
      query: require('@/gql/queries/openpayments.graphql'),
      variables: function () {
        return {
          node: this.selectedNode,
        }
      },
      fetchPolicy: 'network-only',
    },
  },
}
</script>

<style scoped>
.doughnut_legendbox {
  height: 100%;
  margin-left: 30px;
  width: 240px;
}

.legend_row {
  display: flex;
  flex-direction: row;
  margin-bottom: 4px;
}

.legend_color {
  height: 20px;
  margin-right: 15px;
  width: 20px;
}

.legend_amount {
  font-weight: bold;
  margin-right: 15px;
  width: 100px;
}

.legend_row .legend_amount {
  text-align: right;
}

.legend_label {
  color: #919090;
  font-size: 0.9rem;
}

.bgcolor_open {
  background-color: #82c8e2;
}

.bgcolor_due {
  background-color: #ef8305;
}

.bgcolor_overdue {
  background-color: #f82c06;
}

.bgcolor_paid {
  background-color: #00a82d;
}

.doughnut_counter {
  color: #1f78b4;
  font-size: 48px;
  font-weight: 700;
  height: 102px;
  margin-top: 78px;
  position: absolute;
  text-align: center;
  width: 180px;
}

a {
  color: inherit !important;
  text-decoration: none;
}
</style>
